/**
 * ADP SM Cookie Refresh — keeps the SMSESSION cookie alive for multi-app SSO.
 *
 * Per ADP docs the SM Cookie Refresh API must be called every 60 seconds while
 * the user is active. A non-success response means the ADP session expired
 * (e.g. user was idle for 20 min or logged out of another ADP app), and our
 * app must log the user out.
 *
 * Behaviour:
 * - Polls every 60 s while the tab is visible (skips hidden tabs).
 * - On tab focus: fires an immediate refresh to validate the session.
 * - On tab blur: pauses the interval to avoid unnecessary requests.
 * - Can be paused/resumed externally (used by useOidcIdleGuard).
 * - Each tick skips non-OIDC sessions (staff/SAML) automatically.
 * - Missing aimSmCookieRefreshUrl in an OIDC session is treated as a failure.
 * - Any failure (network, non-200, Status !== 'Success') → logout.
 *
 * Uses native fetch (not axios) because the request goes cross-origin to ADP's
 * domain and must include the SMSESSION cookie via credentials: 'include'.
 */
import { revokeOidcToken } from '@/api/auth';
import { useAppConfigStore } from '@/stores/appConfig';
import { useAuthStore } from '@/stores/auth';
import { ACCESS_TOKEN } from '@/types';
import { clearTokens, getIsiSessionId, isOidcSession, setIsiSessionId } from '@/utils/helpers';

const SM_COOKIE_REFRESH_INTERVAL_MS = 60_000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1_000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let listening = false;
let paused = false;

const isTransientError = (status: number): boolean => status >= 500;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const clearSmRefreshInterval = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const startInterval = () => {
  if (intervalId !== null) return;
  intervalId = setInterval(refreshSmCookie, SM_COOKIE_REFRESH_INTERVAL_MS);
};

const handleRefreshFailure = async () => {
  clearSmRefreshInterval();
  try {
    const authStore = useAuthStore();
    await authStore.logout();
  } catch {
    window.location.href = '/v3/welcome-back/';
  }
};

const refreshSmCookie = async (): Promise<boolean> => {
  if (!localStorage.getItem(ACCESS_TOKEN)) return false;
  if (!isOidcSession()) return false;

  const appConfigStore = useAppConfigStore();

  if (!appConfigStore.aimSmCookieRefreshUrl) {
    handleRefreshFailure();
    return false;
  }

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(appConfigStore.aimSmCookieRefreshUrl, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        if (isTransientError(response.status) && attempt < MAX_RETRIES) {
          await delay(RETRY_DELAY_MS);
          continue;
        }
        handleRefreshFailure();
        return false;
      }

      const isiSessionId = response.headers.get('isisessionid');
      if (isiSessionId) {
        const previousId = getIsiSessionId();
        if (previousId && previousId !== isiSessionId) {
          // Session ID changed -> we need to kill current session WITHOUT killing sm session cookie
          // since it contains now a session for a new user
          // So we just need to revoke access token and start OIDC login flow again (by redirecting to welcome page)
          clearSmRefreshInterval();
          await revokeOidcToken().catch(() => {});
          clearTokens();
          window.location.href = '/v3/welcome-back/';
          return false;
        }
        setIsiSessionId(isiSessionId);
      }

      const data = await response.json();

      if (data.Status !== 'Success') {
        handleRefreshFailure();
        return false;
      }
      return true;
    } catch {
      if (attempt < MAX_RETRIES) {
        await delay(RETRY_DELAY_MS);
        continue;
      }
      handleRefreshFailure();
      return false;
    }
  }
  return false;
};

const onVisibilityChange = () => {
  if (paused) return;

  if (document.visibilityState === 'visible') {
    refreshSmCookie();
    startInterval();
  } else {
    clearSmRefreshInterval();
  }
};

export const startSmCookieRefresh = (): void => {
  paused = false;

  if (document.visibilityState === 'visible') {
    refreshSmCookie();
    startInterval();
  }

  if (!listening) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    listening = true;
  }
};

export const pauseSmCookieRefresh = (): void => {
  paused = true;
  clearSmRefreshInterval();
};

export const resumeSmCookieRefresh = async (): Promise<boolean> => {
  paused = false;
  const success = await refreshSmCookie();
  if (success) {
    startInterval();
  }
  return success;
};

export const stopSmCookieRefresh = (): void => {
  paused = false;
  clearSmRefreshInterval();

  if (listening) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listening = false;
  }
};
